<script lang="ts" setup>
import {computed, reactive} from "vue";
import {useRoute, useRouter} from "vue-router";
import useSwr from "@/core/composables/swr/useSwr";
import {loyaltyCardApi} from "@/container";
import {currencyze} from "@/core/filters/Currencyze";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import Icon from "@/core/components/icon/Icon.vue";

const props = defineProps<{
    loyaltyCardId: string;
}>();

const route = useRoute();
const router = useRouter();

const resourceId = Number(props.loyaltyCardId);

const searchParams = reactive<{ page: number; itemsPerPage: number }>({
    page: 1,
    itemsPerPage: 20
});

const swrKey = computed(() => (
    `loyalty-card/${resourceId}/rewards?page=${searchParams.page}&itemsPerPage=${searchParams.itemsPerPage}`
));

const {data: rewards, isLoading, isValidating} = useSwr(
    swrKey,
    () => loyaltyCardApi().getRewards(resourceId, searchParams.page, searchParams.itemsPerPage)
);

const rows = computed(() => (rewards.value?.data ?? []).map((reward) => ({
    id: reward.id,
    customer: reward.customer ?? "—",
    date: reward.date ?? "—",
    label: reward.label,
    value: reward.value != null ? currencyze(reward.value) : "—"
})));

const goBack = () => {
    router.push({name: "program.detail.content", params: {programId: route.params.programId}});
};

const handlePageChange = (page: number) => {
    searchParams.page = page;
};

const handleItemPerPageChange = (itemsPerPage: number) => {
    searchParams.page = 1;
    searchParams.itemsPerPage = itemsPerPage;
};

const headers: Array<{ title: string; key: string; align?: 'start' | 'center' | 'end' }> = [
    {title: 'Client', key: 'customer'},
    {title: 'Date', key: 'date'},
    {title: 'Produit offert', key: 'label'},
    {title: 'Valeur', key: 'value', align: 'end'}
];
</script>

<template>
  <BaseContainer>
    <div class="m-6 flex flex-col gap-4">
      <div
        class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary cursor-pointer w-fit"
        @click="goBack"
      >
        <Icon name="mdi-arrow-left" :size="18" color="#3513e2" />Retour à la vue d'ensemble
      </div>

      <div>
        <h2 class="text-lg font-bold text-primary">
          Récompenses délivrées
        </h2>
        <p class="text-[13px] text-slate-500 mt-0.5">
          {{ rewards?.meta?.total ?? 0 }} récompense(s) obtenue(s)
        </p>
      </div>

      <BaseCard>
        <v-data-table-server
          :headers="headers"
          :items="rows"
          :page="searchParams.page"
          :items-per-page="Number(searchParams.itemsPerPage)"
          :loading="isLoading || isValidating"
          :disable-sort="true"
          item-value="id"
          :items-length="rewards?.meta?.total ?? 0"
          loading-text="Chargement en cours..."
          no-data-text="Aucune récompense délivrée pour le moment"
          :footer-props="{
            'items-per-page-text': 'Éléments par page',
            'page-text': '{0}-{1} sur {2}',
            'total-items': 'Total des éléments'
          }"
          @update:page="handlePageChange"
          @update:items-per-page="handleItemPerPageChange"
        />
      </BaseCard>
    </div>
  </BaseContainer>
</template>
